import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';

import TopBar from '../../../../../components/utils/TopBar';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import {
  SimulateTariffBIZcoreTD,
  GetCh99DeclarationRequirementsBIZcoreTD
} from '../../../../../redux/actions/business/core/scCh99RuleEngineActions';
import { countryList } from '../../../../../components/utils/countryCodeList';

const COUNTRY_OPTIONS = [
  { code: 'CN', name: 'China' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'IN', name: 'India' },
  { code: 'KR', name: 'South Korea' },
  { code: 'JP', name: 'Japan' },
  { code: 'DE', name: 'Germany' },
  { code: 'MX', name: 'Mexico' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'RU', name: 'Russia' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'TH', name: 'Thailand' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'IT', name: 'Italy' },
  { code: 'FR', name: 'France' },
  { code: 'BR', name: 'Brazil' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'TR', name: 'Turkey' },
  { code: 'PH', name: 'Philippines' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'CL', name: 'Chile' },
  { code: 'IL', name: 'Israel' },
  { code: 'AU', name: 'Australia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'EU', name: 'European Union' },
];

const TRANSPORT_MODES = ['Ocean', 'Air', 'Truck', 'Rail', 'Mail', 'Pipeline', 'Other'];

// Section 232 origin declarations. CBP reports an unknown melt/pour or smelt/cast
// origin as "UN", so that leads the list and is the default.
const UNKNOWN_ORIGIN = { code: 'UN', name: 'Unknown' };
const METAL_ORIGIN_COUNTRIES = [
  UNKNOWN_ORIGIN,
  ...countryList.map(c => ({ code: c.code, name: c.name })),
];

const INITIAL_FORM = {
  htsCode: '',
  shipmentValue: '',
  countryOfOrigin: null,
  modeOfTransport: 'Ocean',
  entryDate: '',
  dateOfLoading: '',
};

const PROGRAM_META = {
  SECTION_232: { label: 'Section 232 — Metals', color: '#8b5cf6' },
  SECTION_301: { label: 'Section 301', color: '#f59e0b' },
  SECTION_201: { label: 'Section 201 — Safeguard', color: '#ec4899' },
  IEEPA:       { label: 'IEEPA / Reciprocal', color: '#ef4444' },
  ADCVD:       { label: 'AD/CVD', color: '#06b6d4' },
  CHAPTER_99:  { label: 'Chapter 99 — Other', color: '#3b82f6' },
};

const autocompleteSx = {
  '& .MuiAutocomplete-popupIndicator': { color: '#94a3b8' },
  '& .MuiAutocomplete-clearIndicator': { color: '#94a3b8' },
};

const autocompleteSlotProps = {
  paper: {
    sx: {
      backgroundColor: '#1e293b',
      color: '#e2e8f0',
      border: '1px solid #334155',
      '& .MuiAutocomplete-option': {
        '&:hover': { backgroundColor: '#334155' },
        '&[aria-selected="true"]': { backgroundColor: '#1e3a5f' },
      },
    },
  },
};

const RULE_STATUS_STYLES = {
  APPLIED:        { label: 'Applied',        backgroundColor: '#166534', color: '#4ade80' },
  SUPPRESSED:     { label: 'Suppressed',     backgroundColor: '#7f1d1d', color: '#fca5a5' },
  REPLACED:       { label: 'Replaced',       backgroundColor: '#7f1d1d', color: '#fca5a5' },
  PREEMPTED:      { label: 'Preempted',      backgroundColor: '#7f1d1d', color: '#fca5a5' },
  PENDING_RATE:   { label: 'Rate pending',   backgroundColor: '#78350f', color: '#fcd34d' },
  NEEDS_INPUT:    { label: 'Needs input',    backgroundColor: '#92400e', color: '#fcd34d' },
  INFORMATIONAL:  { label: 'Informational',  backgroundColor: '#1e3a5f', color: '#93c5fd' },
  NOT_APPLICABLE: { label: 'N/A',            backgroundColor: '#1e293b', color: '#64748b' },
};

const TariffSimulatorPage = () => {
  const dispatch = useDispatch();

  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Which metal-origin declarations this HTS code needs. Resolved from the rules
  // that reach it, so the fields appear only where they mean something.
  const [requirements, setRequirements] = useState([]);
  const [declarations, setDeclarations] = useState({});

  useEffect(() => {
    const code = form.htsCode.trim();
    if (code.replace(/[^0-9]/g, '').length < 6) {
      setRequirements([]);
      return;
    }
    const timer = setTimeout(() => {
      dispatch(GetCh99DeclarationRequirementsBIZcoreTD(code, setRequirements));
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.htsCode]);

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleReset = () => {
    setForm({ ...INITIAL_FORM });
    setDeclarations({});
    setRequirements([]);
    setResult(null);
  };

  const handleCalculate = () => {
    if (!canCalculate) return;
    setResult(null);
    dispatch(SimulateTariffBIZcoreTD({
      hts_code:          form.htsCode,
      country_of_origin: form.countryOfOrigin.code,
      entry_date:        form.entryDate,
      date_of_loading:   form.dateOfLoading,
      entered_value:     form.shipmentValue,
      mode_of_transport: form.modeOfTransport,
      ...Object.fromEntries(
        Object.entries(declarations).map(([field, country]) => [field, country?.code || ''])
      ),
    }, setLoading, setResult));
  };

  const canCalculate = form.htsCode && form.countryOfOrigin && form.entryDate;
  const answered = result && (result.status === 'OK' || result.status === 'NEEDS_INPUT');

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: '#334155' },
      '&:hover fieldset': { borderColor: '#64748b' },
      '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
      backgroundColor: '#0f172a',
      color: '#e2e8f0',
    },
    '& .MuiInputLabel-root': { color: '#94a3b8' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#3b82f6' },
    '& .MuiSelect-icon': { color: '#94a3b8' },
  };

  return (
    <Box>
      <TopBar title={"Tariff Simulator"} />

      <Box className="block" p={"24px"}>
        <Box display={"flex"} gap={"32px"} flexWrap={"wrap"}>

          {/* ── LEFT — Shipment Details ────────────────────────────────────── */}
          <Box flex={"1 1 420px"} minWidth={"380px"} maxWidth={"520px"}>
            <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} mb={"16px"}>
              <Typography variant="subtitle2" color="#94a3b8">Shipment Details</Typography>
              <Button size="small" variant="text" startIcon={<RestartAltOutlinedIcon />}
                onClick={handleReset} sx={{ color: '#64748b', textTransform: 'none' }}>
                Reset
              </Button>
            </Box>

            <Box display={"flex"} flexDirection={"column"} gap={"18px"}>

              <TextField label="HTS Code" placeholder="e.g. 7318.15.2010" size="small" fullWidth
                value={form.htsCode} onChange={handleChange('htsCode')} sx={inputSx} />

              <TextField label="Shipment Value (USD)" placeholder="e.g. 100000" size="small" fullWidth
                type="number" value={form.shipmentValue} onChange={handleChange('shipmentValue')} sx={inputSx} />

              <Autocomplete
                options={COUNTRY_OPTIONS}
                getOptionLabel={(o) => `${o.name} (${o.code})`}
                value={form.countryOfOrigin}
                onChange={(_, v) => setForm(prev => ({ ...prev, countryOfOrigin: v }))}
                renderInput={(params) => (
                  <TextField {...params} label="Country of Origin" placeholder="Select country" size="small" sx={inputSx} />
                )}
                sx={autocompleteSx}
                slotProps={autocompleteSlotProps}
              />

              <TextField label="Mode of Transport" size="small" fullWidth select
                value={form.modeOfTransport} onChange={handleChange('modeOfTransport')} sx={inputSx}>
                {TRANSPORT_MODES.map(m => (
                  <MenuItem key={m} value={m} sx={{ backgroundColor: '#1e293b', color: '#e2e8f0', '&:hover': { backgroundColor: '#334155' } }}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>

              <TextField label="Entry Date" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }}
                value={form.entryDate} onChange={handleChange('entryDate')} sx={inputSx} />

              <TextField label="Date of Loading" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }}
                value={form.dateOfLoading} onChange={handleChange('dateOfLoading')} sx={inputSx} />

              {requirements.map((req) => (
                <Autocomplete
                  key={req.field}
                  options={METAL_ORIGIN_COUNTRIES}
                  getOptionLabel={(o) => `${o.name} (${o.code})`}
                  isOptionEqualToValue={(o, v) => o.code === v?.code}
                  value={declarations[req.field] || UNKNOWN_ORIGIN}
                  onChange={(_, v) => setDeclarations(prev => ({ ...prev, [req.field]: v || UNKNOWN_ORIGIN }))}
                  renderInput={(params) => (
                    <TextField {...params} label={req.label} size="small" helperText={req.reason} sx={inputSx} />
                  )}
                  sx={autocompleteSx}
                  slotProps={autocompleteSlotProps}
                />
              ))}

              <Button variant="contained"
                startIcon={loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <CalculateOutlinedIcon />}
                disabled={!canCalculate || loading} onClick={handleCalculate}
                sx={{
                  alignSelf: 'flex-start', backgroundColor: '#3b82f6',
                  '&:hover': { backgroundColor: '#2563eb' },
                  '&.Mui-disabled': { backgroundColor: '#1e3a5f', color: '#64748b' },
                }}>
                {loading ? 'Calculating...' : 'Calculate Duties'}
              </Button>
            </Box>
          </Box>

          {/* ── RIGHT — Results ────────────────────────────────────────────── */}
          <Box flex={"1 1 480px"} minWidth={"400px"}>
            <Typography variant="subtitle2" color="#94a3b8" mb={"16px"}>Results</Typography>

            {!result && !loading && (
              <Box sx={{
                border: '1px dashed #334155', borderRadius: '8px', display: 'flex',
                alignItems: 'center', justifyContent: 'center', minHeight: '400px',
                flexDirection: 'column', gap: '8px',
              }}>
                <CalculateOutlinedIcon sx={{ fontSize: 48, color: '#334155' }} />
                <Typography variant="body1" color="#475569">
                  Complete the shipment details to calculate import duties
                </Typography>
              </Box>
            )}

            {loading && (
              <Box sx={{
                border: '1px dashed #334155', borderRadius: '8px', display: 'flex',
                alignItems: 'center', justifyContent: 'center', minHeight: '400px',
                flexDirection: 'column', gap: '12px',
              }}>
                <CircularProgress size={36} sx={{ color: '#3b82f6' }} />
                <Typography variant="body2" color="#64748b">Evaluating compiled Ch.99 rules...</Typography>
              </Box>
            )}

            {result && (
              <Box display={"flex"} flexDirection={"column"} gap={"16px"}>

                {/* Out of scope / nothing compiled — never shown as a $0.00 answer */}
                {!answered && (
                  <Box sx={{ border: '1px solid #92400e', borderRadius: '8px', overflow: 'hidden', background: '#451a03' }}>
                    <Box sx={{ px: '16px', py: '12px', background: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ReportProblemOutlinedIcon sx={{ fontSize: 18, color: '#fff' }} />
                      <Typography variant="subtitle2" color="#fff">
                        {result.status === 'NO_RULES_LOADED' ? 'No compiled rules in this environment' : 'Outside compiled coverage'}
                      </Typography>
                    </Box>
                    <Box sx={{ px: '16px', py: '14px' }}>
                      <Typography variant="body2" color="#fcd34d">{result.message}</Typography>
                      {result.ch99Codes?.length > 0 && (
                        <Typography variant="caption" color="#fbbf24" sx={{ display: 'block', mt: '8px' }}>
                          Mapped Ch.99 code(s): {result.ch99Codes.join(', ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {answered && (
                  <>
                    {/* Header */}
                    <Box sx={{ border: '1px solid #334155', borderRadius: '8px', overflow: 'hidden' }}>
                      <Box sx={{ px: '16px', py: '12px', background: '#1e3a5f' }}>
                        <Typography variant="subtitle2" color="#fff">{result.htsCode}</Typography>
                        <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', mt: '2px' }}>
                          {result.message}
                        </Typography>
                      </Box>
                      <Box sx={{ px: '16px', py: '10px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                        {[
                          { label: 'Origin', value: result.countryOfOrigin },
                          { label: 'Entry Date', value: result.entryDate },
                          { label: 'Ch.99 Codes', value: (result.ch99Codes || []).join(', ') || '—' },
                          { label: 'Revision', value: result.revision?.revision_name || '—' },
                        ].map((item, i) => (
                          <Box key={i}>
                            <Typography variant="caption" color="#64748b">{item.label}</Typography>
                            <Typography variant="body2" color="#e2e8f0">{item.value}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Total */}
                    <Box sx={{
                      border: `1px solid ${result.status === 'NEEDS_INPUT' ? '#92400e' : '#166534'}`,
                      borderRadius: '8px', overflow: 'hidden',
                      background: result.status === 'NEEDS_INPUT'
                        ? 'linear-gradient(135deg, #451a03 0%, #78350f 100%)'
                        : 'linear-gradient(135deg, #052e16 0%, #14532d 100%)',
                    }}>
                      <Box sx={{ px: '20px', py: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" color={result.status === 'NEEDS_INPUT' ? '#fbbf24' : '#4ade80'}>
                            {result.status === 'NEEDS_INPUT' ? 'Ch.99 Duty Rate (incomplete)' : 'Total Ch.99 Duty Rate'}
                          </Typography>
                          <Typography variant="h4" color="#fff" fontWeight={700}>{result.totalEffectiveRate}</Typography>
                        </Box>
                        <Box textAlign={"right"}>
                          <Typography variant="caption" color={result.status === 'NEEDS_INPUT' ? '#fbbf24' : '#4ade80'}>
                            Estimated Duty
                          </Typography>
                          <Typography variant="h5" color="#fff" fontWeight={700}>{result.totalEstimatedDuty}</Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Landed cost: duties plus the user fees on the entered value */}
                    {result.costBreakdown && (
                      <Box sx={{ border: '1px solid #334155', borderRadius: '8px', overflow: 'hidden' }}>
                        <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid #334155', background: '#0f172a' }}>
                          <Typography variant="subtitle2" color="#94a3b8">Cost Breakdown</Typography>
                        </Box>
                        <Box sx={{ px: '16px', py: '12px' }}>
                          {[
                            { label: 'Base Cost', value: result.costBreakdown.baseCost },
                            { label: 'Total Duties', value: result.costBreakdown.totalDuties },
                            {
                              label: `Harbor Maintenance Fee (HMF) · ${result.costBreakdown.harborMaintenanceFeeRate}`,
                              value: result.costBreakdown.harborMaintenanceFee,
                            },
                            {
                              label: `Merchandise Processing Fee (MPF) · ${result.costBreakdown.merchandiseProcessingFeeRate}`,
                              value: result.costBreakdown.merchandiseProcessingFee,
                            },
                          ].map((item, i) => (
                            <Box key={i} display={"flex"} justifyContent={"space-between"} alignItems={"center"} mb={"6px"}>
                              <Typography variant="body2" color="#64748b">{item.label}</Typography>
                              <Typography variant="body2" color="#e2e8f0">{item.value}</Typography>
                            </Box>
                          ))}
                          <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}
                            sx={{ borderTop: '1px solid #334155', mt: '10px', pt: '10px' }}>
                            <Typography variant="body2" color="#e2e8f0" fontWeight={600}>Landed Cost</Typography>
                            <Typography variant="body1" color="#4ade80" fontWeight={700}>
                              {result.costBreakdown.landedCost}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Base duty + programme breakdown */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <Box sx={{ border: '1px solid #334155', borderRadius: '8px', px: '14px', py: '12px' }}>
                        <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', mb: '4px' }}>
                          Base Duty (Col 1 General)
                        </Typography>
                        <Typography variant="h6" color="#3b82f6" fontWeight={700}>{result.baseDutyRate}</Typography>
                        {result.baseDutyAmount && (
                          <Typography variant="body2" color="#e2e8f0">{result.baseDutyAmount}</Typography>
                        )}
                        <Typography variant="caption" color="#64748b" sx={{ display: 'block', mt: '2px' }}>
                          Resolved from the HTS table
                        </Typography>
                      </Box>

                      {(result.programBreakdown || []).map((bucket, i) => {
                        const meta = PROGRAM_META[bucket.program] || { label: bucket.program, color: '#3b82f6' };
                        return (
                          <Box key={i} sx={{
                            border: `1px solid ${bucket.active ? meta.color + '40' : '#334155'}`,
                            borderRadius: '8px', px: '14px', py: '12px',
                            background: bucket.active ? meta.color + '10' : 'transparent',
                          }}>
                            <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', mb: '4px' }}>
                              {meta.label}
                            </Typography>
                            <Typography variant="h6" color={bucket.active ? meta.color : '#64748b'} fontWeight={700}>
                              {bucket.rate}
                            </Typography>
                            <Typography variant="body2" color={bucket.active ? '#e2e8f0' : '#475569'}>
                              {bucket.amount}
                            </Typography>
                            <Typography variant="caption" color="#64748b" sx={{ display: 'block', mt: '2px' }}>
                              {bucket.rules.join(', ')}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Duty basis */}
                    {result.dutyBasis && (
                      <Box sx={{ border: '1px solid #334155', borderRadius: '8px', overflow: 'hidden' }}>
                        <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid #334155', background: '#0f172a' }}>
                          <Typography variant="subtitle2" color="#94a3b8">Duty Basis</Typography>
                        </Box>
                        <Box sx={{ px: '16px', py: '12px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                          {[
                            { label: 'Entered value', value: result.dutyBasis.enteredValue },
                            { label: 'Steel content', value: result.dutyBasis.steelContentValue },
                            { label: 'Aluminum content', value: result.dutyBasis.aluminumContentValue },
                            { label: 'Copper content', value: result.dutyBasis.copperContentValue },
                            { label: 'Non-metal remainder', value: result.dutyBasis.nonMetalContentValue },
                          ].filter(i => i.value).map((item, i) => (
                            <Box key={i}>
                              <Typography variant="caption" color="#64748b">{item.label}</Typography>
                              <Typography variant="body2" color="#e2e8f0" fontWeight={600}>{item.value}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Rates the substrate does not carry */}
                    {result.conversionGaps?.length > 0 && (
                      <Box sx={{ border: '1px solid #78350f', borderRadius: '8px', overflow: 'hidden' }}>
                        <Box sx={{ px: '16px', py: '10px', background: '#78350f' }}>
                          <Typography variant="subtitle2" color="#fff">
                            Rules that matched but whose rate is not in this revision's extract
                          </Typography>
                        </Box>
                        <Box sx={{ px: '16px', py: '12px', background: '#451a03' }}>
                          {result.conversionGaps.map((gap, i) => (
                            <Box key={i} mb={"8px"}>
                              <Typography variant="body2" color="#fcd34d" fontWeight={600}>
                                {gap.ch99Code} — {gap.citation}
                              </Typography>
                              <Typography variant="caption" color="#fbbf24">{gap.reason}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Facts an entry line cannot carry, resolved by a stated default */}
                    {result.assumptions?.length > 0 && (
                      <Box sx={{ border: '1px solid #1e3a5f', borderRadius: '8px', overflow: 'hidden' }}>
                        <Box sx={{ px: '16px', py: '10px', background: '#1e3a5f' }}>
                          <Typography variant="subtitle2" color="#fff">Assumptions</Typography>
                        </Box>
                        <Box sx={{ px: '16px', py: '12px', background: '#0f172a' }}>
                          {result.assumptions.map((item, i) => (
                            <Box key={i} mb={"8px"}>
                              <Typography variant="body2" color="#93c5fd" fontWeight={600}>{item.label}</Typography>
                              <Typography variant="caption" color="#64748b">{item.reason}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Missing facts */}
                    {result.needsInput?.length > 0 && (
                      <Box sx={{ border: '1px solid #92400e', borderRadius: '8px', overflow: 'hidden' }}>
                        <Box sx={{ px: '16px', py: '10px', background: '#78350f' }}>
                          <Typography variant="subtitle2" color="#fff">Entry facts required to complete this answer</Typography>
                        </Box>
                        <Box sx={{ px: '16px', py: '12px', background: '#451a03' }}>
                          {result.needsInput.map((item, i) => (
                            <Box key={i} mb={"8px"}>
                              <Typography variant="body2" color="#fcd34d" fontWeight={600}>{item.label}</Typography>
                              <Typography variant="caption" color="#fbbf24">{item.reason}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}

                {/* Rule-by-rule trace */}
                {result.appliedRules?.length > 0 && (
                  <Box sx={{ border: '1px solid #334155', borderRadius: '8px', overflow: 'hidden' }}>
                    <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid #334155', background: '#0f172a' }}>
                      <Typography variant="subtitle2" color="#94a3b8">Rules Evaluated</Typography>
                    </Box>
                    {result.appliedRules.map((rule, i) => {
                      const style = RULE_STATUS_STYLES[rule.status] || RULE_STATUS_STYLES.NOT_APPLICABLE;
                      return (
                        <Box key={i} sx={{
                          px: '16px', py: '10px',
                          borderBottom: i < result.appliedRules.length - 1 ? '1px solid #1e293b' : 'none',
                          borderLeft: rule.isIeepa ? '3px solid #ef4444' : '3px solid transparent',
                          background: rule.isIeepa ? '#ef444410' : 'transparent',
                        }}>
                          <Box display={"flex"} justifyContent={"space-between"} alignItems={"flex-start"} gap={"12px"}>
                            <Box flex={1} minWidth={0}>
                              <Box display="flex" alignItems="center" gap="8px">
                                {rule.isIeepa && (
                                  <Chip label="IEEPA" size="small"
                                    sx={{ backgroundColor: '#7f1d1d', color: '#fca5a5', fontSize: '10px', height: '18px', fontWeight: 700 }} />
                                )}
                                <Typography variant="body2" color="#e2e8f0">{rule.authority}</Typography>
                              </Box>
                              <Typography variant="caption" color="#64748b" sx={{ display: 'block' }}>
                                {rule.displayCode || rule.ch99Code}{rule.sourceNode ? ` · ${rule.sourceNode}` : ''}
                              </Typography>
                              {rule.reason && (
                                <Typography variant="caption" color="#475569" sx={{ display: 'block', mt: '2px' }}>
                                  {rule.reason}
                                </Typography>
                              )}
                            </Box>
                            <Box display={"flex"} alignItems={"center"} gap={"12px"} flexShrink={0}>
                              <Box textAlign={"right"}>
                                <Typography variant="body2" color="#e2e8f0" fontWeight={600}>{rule.rate}</Typography>
                                <Typography variant="caption" color="#64748b">{rule.amount}</Typography>
                              </Box>
                              <Chip label={style.label} size="small"
                                sx={{ backgroundColor: style.backgroundColor, color: style.color, fontSize: '11px', height: '22px' }} />
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Interactions */}
                {result.interactions?.length > 0 && (
                  <Box sx={{ border: '1px solid #334155', borderRadius: '8px', overflow: 'hidden' }}>
                    <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid #334155', background: '#0f172a' }}>
                      <Typography variant="subtitle2" color="#94a3b8">Rule Interactions</Typography>
                    </Box>
                    {result.interactions.map((ix, i) => (
                      <Box key={i} sx={{
                        px: '16px', py: '8px',
                        borderBottom: i < result.interactions.length - 1 ? '1px solid #1e293b' : 'none',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
                      }}>
                        <Box minWidth={0}>
                          <Typography variant="caption" color="#e2e8f0">
                            {ix.sourceRule} <span style={{ color: '#64748b' }}>{ix.type}</span> {ix.targetRule}
                          </Typography>
                          {ix.phrase && (
                            <Typography variant="caption" color="#475569" sx={{ display: 'block', fontStyle: 'italic' }}>
                              “{ix.phrase}”
                            </Typography>
                          )}
                        </Box>
                        <Chip label={ix.active ? 'Active' : 'Inactive'} size="small"
                          sx={{
                            backgroundColor: ix.active ? '#166534' : '#1e293b',
                            color: ix.active ? '#4ade80' : '#64748b',
                            fontSize: '11px', height: '20px', flexShrink: 0,
                          }} />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Warnings */}
                {result.warnings?.length > 0 && (
                  <Box sx={{ border: '1px solid #92400e', borderRadius: '8px', px: '16px', py: '12px', background: '#451a03' }}>
                    <Typography variant="caption" color="#fbbf24" fontWeight={600} sx={{ display: 'block', mb: '6px' }}>
                      Warnings
                    </Typography>
                    {result.warnings.map((w, i) => (
                      <Typography key={i} variant="caption" color="#fcd34d" sx={{ display: 'block', mb: '2px' }}>
                        {w}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Divider sx={{ borderColor: '#1e293b' }} />
                <Typography variant="caption" color="#334155">
                  Chapter 99 duties computed from compiled rules in revision{' '}
                  {result.revision?.revision_name || '—'}. Base duty is shown for reference; MPF and HMF are not included.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TariffSimulatorPage;
